import { useEffect, useReducer, useState } from "react";
import {
  type EditHistory,
  type NoteEdit,
  applyEdits,
  emptyHistory,
  redo,
  undo,
} from "../patterns/applyEdit.js";
import { PianoRoll, type PianoRollTool } from "./PianoRoll.js";
import type { Midi, MidiNote } from "@midi-fantom-utils/midi-core";
import type { Pattern } from "@midi-fantom-utils/midi-util";

export type PatternEditorProps = {
  open: boolean;
  pattern: Pattern;
  onSave: (midi: Midi) => void;
  onClose: () => void;
};

type EditorState = {
  midi: Midi;
  history: EditHistory;
};

type EditorAction =
  | { type: "apply"; edits: NoteEdit[] }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; midi: Midi };

const reducer = (state: EditorState, action: EditorAction): EditorState => {
  switch (action.type) {
    case "apply": {
      const { midi, history } = applyEdits(state.midi, action.edits, state.history);
      return { midi, history };
    }
    case "undo": {
      const r = undo(state.midi, state.history);
      return r ? r : state;
    }
    case "redo": {
      const r = redo(state.midi, state.history);
      return r ? r : state;
    }
    case "reset":
      return { midi: action.midi, history: emptyHistory() };
    default:
      return state;
  }
};

const DENOMINATORS = [1, 2, 4, 8, 16, 32, 64] as const;

export const PatternEditor = ({ open, pattern, onSave, onClose }: PatternEditorProps) => {
  const [state, dispatch] = useReducer(reducer, {
    midi: pattern.midi,
    history: emptyHistory(),
  });
  const [tool, setTool] = useState<PianoRollTool>("draw");
  const [snapDen, setSnapDen] = useState<number | null>(4);

  // Reset draft when opened with a new pattern.
  useEffect(() => {
    if (open) {
      dispatch({ type: "reset", midi: pattern.midi });
    }
  }, [open, pattern]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) dispatch({ type: "redo" });
        else dispatch({ type: "undo" });
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
        e.preventDefault();
        dispatch({ type: "redo" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleAdd = (note: Omit<MidiNote, "channel">) => {
    dispatch({ type: "apply", edits: [{ type: "add", note }] });
  };
  const handleMove = (idx: number, ticks: number, midi: number) => {
    dispatch({ type: "apply", edits: [{ type: "move", noteId: idx, ticks, midi }] });
  };
  const handleResize = (idx: number, durationTicks: number) => {
    dispatch({ type: "apply", edits: [{ type: "resize", noteId: idx, durationTicks }] });
  };
  const handleErase = (idx: number) => {
    dispatch({ type: "apply", edits: [{ type: "remove", noteId: idx }] });
  };

  const canUndo = state.history.past.length > 0;
  const canRedo = state.history.future.length > 0;
  const noteCount = state.midi.tracks.reduce((s, t) => s + t.notes.length, 0);

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="settings-modal__backdrop"
      onMouseDown={onBackdropMouseDown}
      role="presentation"
    >
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pattern-editor-title"
        style={{ width: "min(900px, 96vw)", maxHeight: "92vh" }}
      >
        <header className="settings-modal__head">
          <h2 id="pattern-editor-title" className="settings-modal__title">
            ✎ {pattern.name} · {pattern.lengthMeasures} тактов · {noteCount} нот
          </h2>
          <button
            type="button"
            className="btn btn--ghost settings-modal__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </header>
        <div className="settings-modal__body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span className="muted">Инструмент:</span>
            {(["draw", "erase", "select"] as PianoRollTool[]).map((t) => (
              <button
                key={t}
                type="button"
                className={`btn ${tool === t ? "btn--primary" : ""}`}
                onClick={() => setTool(t)}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                {t}
              </button>
            ))}
            <span className="muted" style={{ marginLeft: 12 }}>Snap:</span>
            <button
              type="button"
              className={`btn ${snapDen === null ? "btn--primary" : ""}`}
              onClick={() => setSnapDen(null)}
              style={{ padding: "2px 8px", fontSize: 12 }}
            >
              off
            </button>
            {DENOMINATORS.map((d) => (
              <button
                key={d}
                type="button"
                className={`btn ${snapDen === d ? "btn--primary" : ""}`}
                onClick={() => setSnapDen(d)}
                style={{ padding: "2px 8px", fontSize: 12 }}
              >
                1/{d}
              </button>
            ))}
            <button
              type="button"
              className="btn"
              onClick={() => dispatch({ type: "undo" })}
              disabled={!canUndo}
              style={{ marginLeft: 12, padding: "2px 8px", fontSize: 12 }}
            >
              ↶ Undo
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => dispatch({ type: "redo" })}
              disabled={!canRedo}
              style={{ padding: "2px 8px", fontSize: 12 }}
            >
              ↷ Redo
            </button>
          </div>
          <PianoRoll
            midi={state.midi}
            width={860}
            height={300}
            mode="editor"
            tool={tool}
            snapDenominator={snapDen}
            onAddNote={handleAdd}
            onMoveNote={handleMove}
            onResizeNote={handleResize}
            onEraseNote={handleErase}
          />
        </div>
        <footer className="settings-modal__foot">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              onSave(state.midi);
              onClose();
            }}
          >
            Сохранить
          </button>
        </footer>
      </div>
    </div>
  );
};
